import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Send, Loader2, MessageCircleHeart } from "lucide-react";

const SUGGESTED = ["Quanti body servono per la valigia?", "La lana punge il neonato?", "Che taglia compro per la nascita?", "Come lavo i capi la prima volta?"];

const Bubble = ({ m }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
      m.role === "user" ? "bg-[#2D2825] text-white rounded-br-md" : "bg-white border border-[#EAE3DC] text-[#2D2825] rounded-bl-md"}`}>
      {m.text.replace(/\*\*/g, "")}
    </div>
  </div>
);

export const ConsultantChat = ({ open, onOpenChange, lessonId }) => {
  const { profile } = useProfile();
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [sid] = useState(() => `s${Date.now().toString(36)}`);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const ask = async (text) => {
    const question = (text || q).trim();
    if (!question || busy || !profile) return;
    setQ("");
    setMsgs((m) => [...m, { id: `u-${Date.now()}`, role: "user", text: question }]);
    setBusy(true);
    try {
      const { data } = await api.post(`/school/${profile.id}/ask`, { question, session_id: sid, lesson_id: lessonId || null });
      setMsgs((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", text: data.answer }]);
    } catch (e) {
      setMsgs((m) => [...m, { id: `e-${Date.now()}`, role: "assistant", text: e.response?.data?.detail || "Scusa, riprova tra un attimo." }]);
    } finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="consultant-chat" className="w-full sm:max-w-md bg-[#FAF8F5] flex flex-col p-0">
        <SheetHeader className="p-5 border-b border-[#EAE3DC] text-left">
          <SheetTitle className="font-serif text-2xl text-[#2D2825] flex items-center gap-2"><MessageCircleHeart className="text-[#C87D75]" /> La Consulente Minime</SheetTitle>
          <SheetDescription className="text-[#8C7F78]">Chiedimi tutto su taglie, tessuti, valigia e cura dei capi. Rispondo pensando al tuo bimbo.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="consultant-messages">
          {msgs.length === 0 && (
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-widest text-[#8C7F78]">Domande frequenti</div>
              {SUGGESTED.map((s) => (
                <button key={s} onClick={() => ask(s)} data-testid="consultant-suggestion"
                  className="block w-full text-left rounded-xl bg-white border border-[#EAE3DC] hover:border-[#E5A8A0] px-4 py-3 text-sm text-[#5C534E] transition-colors">{s}</button>
              ))}
            </div>
          )}
          {msgs.map((m) => <Bubble key={m.id} m={m} />)}
          {busy && <div className="flex items-center gap-2 text-sm text-[#8C7F78]"><Loader2 size={15} className="animate-spin" /> La consulente sta scrivendo…</div>}
          <div ref={endRef} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="p-4 border-t border-[#EAE3DC] bg-white flex gap-2 pb-safe">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Scrivi la tua domanda…" data-testid="consultant-input" maxLength={600}
            className="flex-1 rounded-full border border-[#EAE3DC] bg-[#FAF8F5] px-4 h-12 text-base sm:text-sm outline-none focus:border-[#E5A8A0]" />
          <button type="submit" disabled={busy || !q.trim()} data-testid="consultant-send" aria-label="Invia"
            className="w-12 h-12 rounded-full bg-[#C87D75] hover:bg-[#B56B63] text-white inline-flex items-center justify-center disabled:opacity-50 transition-colors">
            <Send size={17} />
          </button>
        </form>
      </SheetContent>
    </Sheet>
  );
};
